import React from 'react';
import { motion } from 'framer-motion';

/**
 * BespokeGildingGlazingSection
 *
 * Editorial "The frame, considered" section of `/picture-framing`. Self-
 * contained — purely static markup with Framer Motion reveals. Extracted
 * from `PictureFramingPage.jsx` to bring the page under 500 lines.
 */
const BespokeGildingGlazingSection = () => (
  <section className="py-20 sm:py-28 px-6 bg-white border-b border-border">
    <div className="max-w-[1400px] mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="grid md:grid-cols-12 gap-10 lg:gap-16 mb-14 sm:mb-20"
      >
        <div className="md:col-span-4">
          <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-5">
            The frame, considered
          </p>
        </div>
        <div className="md:col-span-8">
          <h2 className="font-serif font-light leading-[1.05] tracking-tight text-3xl sm:text-5xl mb-7">
            Bespoke gilding &amp;
            <span className="italic"> glazing.</span>
          </h2>
          <p className="text-base text-neutral-600 leading-relaxed max-w-2xl mb-5">
            A frame is not packaging. It is the quiet architecture that
            tells the eye where the artwork begins, holds the light, and
            lasts longer than any of us. Every Zebra One frame is built
            bespoke, finished by hand, and quoted to the work itself —
            never to a catalogue.
          </p>
        </div>
      </motion.div>

      {/* Hero pair of gilded frame images */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-16 sm:mb-20">
        <motion.figure
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="overflow-hidden bg-neutral-100"
        >
          <img
            src="/framing/gilding-22ct-gold.png"
            alt="Hand-gilded 22-carat gold leaf picture frame, Zebra One Gallery framing studio"
            className="w-full h-auto block"
            loading="lazy"
            decoding="async"
            data-testid="framing-gilding-gold-img"
          />
          <figcaption className="pt-3 text-[11px] uppercase tracking-[0.25em] text-neutral-500">
            22ct gold leaf · water-gilded
          </figcaption>
        </motion.figure>
        <motion.figure
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.08 }}
          className="overflow-hidden bg-neutral-100"
        >
          <img
            src="/framing/gilding-silver-leaf.png"
            alt="Hand-gilded silver leaf picture frame, contemporary slim profile"
            className="w-full h-auto block"
            loading="lazy"
            decoding="async"
            data-testid="framing-gilding-silver-img"
          />
          <figcaption className="pt-3 text-[11px] uppercase tracking-[0.25em] text-neutral-500">
            Silver leaf · contemporary profile
          </figcaption>
        </motion.figure>
      </div>

      {/* Two-column craft detail */}
      <div className="grid md:grid-cols-12 gap-10 lg:gap-16">
        {/* LEFT — Bespoke Gilding */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="md:col-span-6 border-t border-neutral-200 pt-8"
        >
          <p className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 mb-4">
            Bespoke gilding
          </p>
          <h3 className="font-serif text-2xl sm:text-3xl leading-[1.2] tracking-tight mb-5">
            Gold, silver, copper —
            <span className="italic"> applied by hand.</span>
          </h3>
          <p className="text-base text-neutral-700 leading-relaxed mb-6">
            We hand-finish frames in 22-carat gold, white gold, palladium,
            silver and copper leaf, using both traditional water gilding
            and oil-gilded techniques. Every leaf is laid on by hand and
            burnished to the depth of finish the work needs — bright and
            polished for a contemporary room, antiqued and rubbed back to
            sit beside a centuries-old piece.
          </p>
          <ul className="space-y-3 text-sm text-neutral-700">
            <li className="flex gap-3">
              <span className="text-neutral-400 mt-1">·</span>
              <span><strong className="font-medium text-neutral-900">22ct &amp; 23.75ct gold leaf</strong> — water-gilded for the deepest, most luminous finish.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-neutral-400 mt-1">·</span>
              <span><strong className="font-medium text-neutral-900">White gold &amp; palladium</strong> — for cooler, contemporary settings.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-neutral-400 mt-1">·</span>
              <span><strong className="font-medium text-neutral-900">Silver &amp; copper leaf</strong> — for warmth, patina, and unconventional briefs.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-neutral-400 mt-1">·</span>
              <span><strong className="font-medium text-neutral-900">Antiqued &amp; distressed finishes</strong> — to sit beside historic works without drawing the eye.</span>
            </li>
          </ul>
        </motion.div>

        {/* RIGHT — Glazing */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.08 }}
          className="md:col-span-6 border-t border-neutral-200 pt-8"
        >
          <p className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 mb-4">
            Glazing
          </p>
          <h3 className="font-serif text-2xl sm:text-3xl leading-[1.2] tracking-tight mb-5">
            From float glass to
            <span className="italic"> museum acrylic.</span>
          </h3>
          <p className="text-base text-neutral-700 leading-relaxed mb-6">
            The glazing carries half the responsibility for how a work
            ages — its protection from UV, its clarity, its weight, its
            reflection. We brief every option openly, then advise the one
            that suits the room and the work, not the invoice.
          </p>
          <ul className="space-y-3 text-sm text-neutral-700">
            <li className="flex gap-3">
              <span className="text-neutral-400 mt-1">·</span>
              <span><strong className="font-medium text-neutral-900">Float glass</strong> — the standard. Clear, economical, no UV protection.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-neutral-400 mt-1">·</span>
              <span><strong className="font-medium text-neutral-900">Tru Vue</strong> — anti-reflective conservation glass. The current gold standard for fine-art framing.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-neutral-400 mt-1">·</span>
              <span><strong className="font-medium text-neutral-900">Art glass</strong> — anti-glare with UV protection, for works hung opposite a window.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-neutral-400 mt-1">·</span>
              <span><strong className="font-medium text-neutral-900">Acrylic</strong> — perspex glazing. Lighter and shatter-resistant, ideal for large works and shipping.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-neutral-400 mt-1">·</span>
              <span><strong className="font-medium text-neutral-900">Museum-grade acrylic</strong> — UV-filtering, anti-static, optically clear. The conservation standard for works on paper and signed prints.</span>
            </li>
          </ul>
        </motion.div>
      </div>

      {/* Footer note */}
      <div className="mt-14 sm:mt-16 pt-8 border-t border-neutral-200 max-w-2xl">
        <p className="text-sm text-neutral-600 leading-relaxed">
          Every frame and glazing pairing is quoted bespoke against the
          work in question. We collect, frame, install and warranty the
          piece — for clients in London directly, and worldwide via our
          fine-art shippers.
        </p>
      </div>
    </div>
  </section>
);

export default BespokeGildingGlazingSection;
